import React, { useEffect, useRef, useState } from 'react';
import { DEFAULT_ROWS_PER_PAGE, FETCH_MORE_DATA_AMOUNT } from '../../../config-global';
import { StatusEnum } from '../../../enums/status';
import { DriverTabPanelEnum } from '../../../enums/driverTabPanelEnum';
import axios from '../../../lib/axios';
import {
  DELETE_PROFILE_RIDER,
  FORM_RIDER_DELETE,
  RIDER_FIND_MANY,
  USER_RIDER_DELETE,
  USER_RIDER_FIND_MANY,
} from '../../../lib/endpoints/rider';
import { DriverModel } from '../../../models/driver';
import { DriverEnum } from '../../../enums/driverEnum';
import { useTranslation } from 'react-i18next';
import { EMPLOYEE_CHANGE_DRIVER_PASSWORD } from '../../../lib/endpoints/auth';

export const useDriverControllers = () => {
  const [tabStatus, setTabStatus] = useState<StatusEnum>(StatusEnum.ALL);
  const [tabDriverPage, setTabDriverPage] = useState<DriverTabPanelEnum>(DriverTabPanelEnum.All);
  const [tabDriverOnline, setTabDriverOnline] = useState<DriverTabPanelEnum>(
    DriverTabPanelEnum.All
  );
  const [selectedDriverType, setSelectedDriverType] = useState<DriverEnum>(DriverEnum.ALL);

  const [data, setData] = useState<DriverModel[]>([]);

  const [selectedRiderId, setSelectedID] = useState<string>(null!);
  const [selectedProfileId, setSelectedProfileId] = useState<string>(null!);
  const [selectedProfile, setSelectedProfile] = useState<DriverModel>(null!);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentFormPage, setCurrentFormPage] = useState<number>(1);
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchUserValue, setSearchUserValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>(null!);

  const [openMenu, setOpenMenu] = useState(null);
  const [openFormRiderUser, setOpenFormRiderUser] = useState<boolean>(false);
  const [openChangePassword, setOpenChangePassword] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [loadingDeleteButton, setLoadingDeleteButton] = useState<boolean>(false);
  const [loadingConfirmButton, setLoadingConfirmButton] = useState<boolean>(false);
  const [openDeleteForm, setOpenDeleteForm] = useState<boolean>(false);

  const ownerPassword = useRef<HTMLInputElement>(null!);
  const newPassword = useRef<HTMLInputElement>(null!);
  const confirmNewPassword = useRef<HTMLInputElement>(null!);

  const { t } = useTranslation('global');

  const handleFetchData = async () => {
    try {
      setLoadingData(true);
      const resUserRider = await axios.post(USER_RIDER_FIND_MANY + `?limit=${100000}`);
      const profile = resUserRider?.data?.data;
      if (resUserRider?.data?.data?.length > 0) {
        setData(profile);
      }
    } catch (err) {
      console.error('this error', err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleDeleteRiderUser = async () => {
    try {
      setOpenMenu(null);
      setLoadingDeleteButton(true);
      await Promise.all([
        axios.delete(USER_RIDER_DELETE + selectedRiderId),
        axios.delete(DELETE_PROFILE_RIDER + selectedProfileId),
      ]);
      await handleFetchData();
      setOpenDeleteForm(false);
    } catch (err: any) {
      try {
        window.alert(err.response.data.message);
      } catch (err2) {
        window.alert('Server error please try again');
      }
    } finally {
      setLoadingDeleteButton(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setLoadingConfirmButton(true);
      await axios.post(
        `${EMPLOYEE_CHANGE_DRIVER_PASSWORD}?ownerPassword=${ownerPassword.current?.value}&riderId=${selectedRiderId}&NewRiderPassword=${newPassword.current?.value}`
      );
      // await handleFetchData();
      handleCloseChangePasswordForm();
      window.alert('ປ່ຽນລະຫັດຜ່ານສຳເລັດເເລ້ວ');
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenMenu(null);
    }
  };

  const handleSubmit = () => {
    if (handleValidate()) {
      handleChangePassword();
    }
  };

  const handleValidate = () => {
    if (!ownerPassword.current?.value) {
      setErrorMessage('Please enter owner password.');
      return false;
    }
    if (!newPassword.current?.value) {
      setErrorMessage('Please enter new password.');
      return false;
    }
    if (!confirmNewPassword.current?.value) {
      setErrorMessage('Please enter confirm new password.');
      return false;
    }
    if (newPassword.current?.value !== confirmNewPassword.current?.value) {
      setErrorMessage('Password and Confirm Password do not match.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleOpenChangePasswordForm = () => {
    setOpenChangePassword(true);
  };

  const handleCloseChangePasswordForm = () => {
    setOpenChangePassword(false);
    setErrorMessage('');
  };

  const handleOpenForm = () => {
    setOpenFormRiderUser(true);
  };

  const handleOpenEditRider = () => {
    setOpenFormRiderUser(true);
    const getData = rowUserRider.filter((pro) => pro.rider?.id === selectedRiderId)[0];
    setSelectedProfile(getData);
  };

  const handleCloseForm = () => {
    setOpenFormRiderUser(false);
    setOpenMenu(null);
  };

  const handleOpenMenu = (event: any, getId: string, proId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedID(getId);
    setSelectedProfileId(proId);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const handleOpenDeleteForm = () => {
    setOpenDeleteForm(true);
  };

  const handleCloseDeleteForm = () => {
    setOpenDeleteForm(false);
  };

  const filterDataByRole: DriverModel[] = data?.filter((item: DriverModel) => {
    if (selectedDriverType === DriverEnum.ALL) {
      return true;
    } else if (selectedDriverType === DriverEnum.FULLTIME) {
      return item?.rider?.type === DriverEnum.FULLTIME;
    } else if (selectedDriverType === DriverEnum.FREELANCER) {
      return item?.rider?.type === DriverEnum.FREELANCER;
    } else {
      return false;
    }
  });

  const filterRiderOnline: DriverModel[] = filterDataByRole?.filter((item: DriverModel) => {
    if (tabDriverOnline === DriverTabPanelEnum.All) {
      return true;
    } else if (tabDriverOnline === DriverTabPanelEnum.Active) {
      return item?.rider?.online === true;
    } else if (tabDriverOnline === DriverTabPanelEnum.Inactive) {
      return item?.rider?.online === false;
    } else {
      return false;
    }
  });

  const filterDataByStatus: DriverModel[] = filterRiderOnline?.filter((item: DriverModel) => {
    if (tabDriverPage === DriverTabPanelEnum.All) {
      return true;
    } else if (tabDriverPage === DriverTabPanelEnum.Active) {
      return item?.rider.temporaryBan === false;
    } else if (tabDriverPage === DriverTabPanelEnum.Inactive) {
      return item?.rider.temporaryBan === true;
    } else {
      return false;
    }
  });

  const filterData: DriverModel[] = filterDataByStatus?.filter(
    (item: DriverModel) =>
      item?.profile?.fullName?.toLowerCase()?.includes(searchUserValue.toLowerCase()) ||
      item?.profile?.phoneNumber?.toLowerCase()?.includes(searchUserValue.toLowerCase())
  );

  const rowUserRider: DriverModel[] = filterData.slice(
    (currentPage - 1) * DEFAULT_ROWS_PER_PAGE,
    currentPage * DEFAULT_ROWS_PER_PAGE
  );

  const handleChangePage = (event: any, newPage: any) => {
    setCurrentPage(newPage);
    setCurrentFormPage(newPage);
  };

  const onSearch = (e: any) => {
    setSearchValue(e.target.value);
  };

  const handleSearchUser = (e: any) => {
    setSearchUserValue(e.target.value);
  };

  const handleSelectedDriverType = (e: any) => {
    setSelectedDriverType(e.target.value);
  };

  const isSearchUserNotFound: boolean = !rowUserRider.length && !!searchUserValue;

  const countPagination = Math.ceil(filterData.length / DEFAULT_ROWS_PER_PAGE);

  const countIsOnlineRider = filterDataByRole?.filter(
    (item: DriverModel) => item?.rider?.online === true
  )?.length;

  const countIsOfflineRider = filterDataByRole?.filter(
    (item: DriverModel) => item?.rider?.online === false
  )?.length;

  useEffect(() => {
    handleFetchData();
  }, []);

  return {
    onSearch,
    handleChangePage,
    rowUserRider,
    currentPage,
    searchValue,
    handleOpenForm,
    handleCloseForm,
    handleOpenMenu,
    openMenu,
    openFormRiderUser,
    handleCloseMenu,
    countPagination,
    currentFormPage,
    handleSetTabStatus: (newValue: StatusEnum) => {
      setTabStatus(newValue);
    },
    tabStatus,
    handleDriverTabChange: (newValue: DriverTabPanelEnum) => setTabDriverPage(newValue),
    tabDriverOnline,
    handleTabRiderOnline: (newValue: DriverTabPanelEnum) => setTabDriverOnline(newValue),
    handleSelectedDriverType,
    selectedDriverType,
    tabDriverPage,
    loadingData,
    handleOpenEditRider,
    selectedProfile,
    handleFetchData,
    handleDeleteRiderUser,
    openDeleteForm,
    loadingDeleteButton,
    handleOpenDeleteForm,
    handleCloseDeleteForm,
    selected: (newValue: DriverModel) => setSelectedProfile(newValue),
    handleSearchUser,
    searchUserValue,
    isSearchUserNotFound,
    handleCloseChangePasswordForm,
    handleOpenChangePasswordForm,
    openChangePassword,
    loadingConfirmButton,
    errorMessage,
    handleSubmit,
    handleChangePassword,
    ownerPassword,
    newPassword,
    confirmNewPassword,
    data,
    countIsOnlineRider,
    countIsOfflineRider,
  };
};
